import { isPlatformError } from '@agentic/contracts';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { Toast as RToast } from 'radix-ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { IdText } from './data.tsx';
import type { Tone } from './primitives.tsx';

export interface ToastInput {
  /** First line: for an error, the problem in one sentence. */
  title: string;
  /** Second line: for an error, what it means for the user's work. */
  description?: string;
  /** Third line: for an error, what the user can do now. */
  next?: string;
  /** Server reference of a failed request, shown as `Ref: <id>` with a copy button. */
  traceId?: string;
  /** PlatformError code, kept in a collapsed "Technical details" disclosure. */
  code?: string;
  tone?: Tone;
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

export interface ToastApi {
  push(toast: ToastInput): void;
  /**
   * Toast for any thrown value: a generic title, the code under "Technical details" and the reference when the error
   * carries one. It never shows the error's message; apps that know better wording `push` it instead.
   * @deprecated The workbench words errors through its presentation dictionary (`errorToast` + `push`); no caller
   * left. Kept for backward compatibility of the ui-kit API.
   */
  error(error: unknown, title?: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_BAR: Record<Tone, string> = {
  neutral: 'bg-ink-3',
  info: 'bg-info',
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  accent: 'bg-accent',
};

/** Provides `useToast()`. Toasts are announced politely (errors assertively) by Radix. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((toast: ToastInput) => {
    setItems((prev) => [...prev.slice(-4), { ...toast, id: Date.now() + Math.random() }]);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      push,
      error(error: unknown, title?: string) {
        const traceId = isPlatformError(error) ? error.details?.traceId : undefined;
        push({
          title: title ?? (isPlatformError(error) ? 'Request failed' : 'Something went wrong'),
          code: isPlatformError(error) ? error.code : 'internal',
          ...(typeof traceId === 'string' && traceId ? { traceId } : {}),
          tone: 'bad',
        });
      },
    }),
    [push],
  );
  return (
    <ToastContext.Provider value={api}>
      <RToast.Provider swipeDirection="right" duration={6000}>
        {children}
        {items.map((t) => (
          <RToast.Root
            key={t.id}
            type={t.tone === 'bad' ? 'foreground' : 'background'}
            duration={t.durationMs ?? (t.tone === 'bad' ? 9000 : 5000)}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
            }}
            className="wb-enter relative flex items-start gap-2.5 overflow-hidden rounded-xl border border-line bg-surface py-3 pr-9 pl-4 shadow-elevated"
            data-testid="toast"
          >
            <span
              aria-hidden
              className={clsx('mt-[7px] size-1.5 shrink-0 rounded-full', TONE_BAR[t.tone ?? 'neutral'])}
            />
            <div className="min-w-0">
              <RToast.Title className="text-[13px] font-semibold text-ink">{t.title}</RToast.Title>
              {(t.description || t.next) && (
                <RToast.Description asChild>
                  <div className="mt-0.5 text-[13px] text-ink-2">
                    {t.description && <p>{t.description}</p>}
                    {t.next && <p data-testid="toast-next">{t.next}</p>}
                  </div>
                </RToast.Description>
              )}
              {t.traceId && <ToastReference traceId={t.traceId} />}
              {t.code && (
                <details className="mt-1 text-[12px] text-ink-3">
                  <summary className="cursor-pointer select-none">Technical details</summary>
                  <code className="font-mono text-[12px]">{t.code}</code>
                </details>
              )}
            </div>
            <RToast.Close
              aria-label="Dismiss"
              className="absolute top-2.5 right-2.5 inline-flex size-6 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <X className="size-3.5" aria-hidden />
            </RToast.Close>
          </RToast.Root>
        ))}
        <RToast.Viewport className="fixed right-4 bottom-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none" />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}

/** `Ref: <id>` line with a copy button; the label for readers and screen readers is "Reference". */
function ToastReference({ traceId }: { traceId: string }) {
  return (
    <p
      className="mt-1 flex items-center gap-1 text-[12px] text-ink-3"
      title="Reference"
      data-testid="toast-reference"
    >
      <span>Ref:</span>
      <IdText value={traceId} copy label="reference" className="text-[12px]" />
    </p>
  );
}

/** Access the toast API. Must be inside `ToastProvider`. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
